import {
	forwardRef,
	type HTMLAttributes,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";
import { useMollieComponent } from "./hooks";
import type { MollieComponentType, MollieChangeEvent } from "./types";
import { cn } from "@bidbattles/bloks/lib/ui";

export interface MollieComponentBaseProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
	type: MollieComponentType;
	onValidate?: (message: string | null) => void;
}

const MollieComponentBase = forwardRef<
	HTMLDivElement | null,
	MollieComponentBaseProps
>(({ type, className, onValidate, ...divProps }, ref) => {
	const innerRef = useRef<HTMLDivElement | null>(null);
	useImperativeHandle(ref, () => {
		if (innerRef.current) {
			return innerRef.current;
		}

		// Horrors beyond human comprehension
		return null as unknown as HTMLDivElement;
	});
	const component = useMollieComponent(type, innerRef);

	useEffect(() => {
		if (onValidate) {
			const handler = (event: MollieChangeEvent) => {
				if (event.error && event.touched) {
					onValidate(event.error);
					return;
				}

				onValidate(null);
			};
			component.addEventListener("change", handler);
			return () => component.removeEventListener("change", handler);
		}
	}, [component, onValidate]);

	return (
		<div
			{...divProps}
			ref={innerRef}
			className={cn("mollie-component-container", className)}
		/>
	);
});
MollieComponentBase.displayName = "MollieComponentBase";

export type MollieComponentProps<_ extends MollieComponentType> = Omit<
	MollieComponentBaseProps,
	"type"
>;

export type MollieReactComponent<Type extends MollieComponentType> = ReturnType<
	typeof createComponent<Type>
>;

function createComponent<Type extends MollieComponentType>(type: Type) {
	const component = forwardRef<
		HTMLDivElement | null,
		MollieComponentProps<Type>
	>((props, ref) => {
		return <MollieComponentBase {...props} type={type} ref={ref} />;
	});
	const capitalized = `${type.charAt(0).toUpperCase()}${type.substring(1)}`;
	component.displayName = `Mollie.${capitalized}`;
	return component;
}

export const Mollie = {
	Card: createComponent("card"),
	CardHolder: createComponent("cardHolder"),
	CardNumber: createComponent("cardNumber"),
	VerificationCode: createComponent("verificationCode"),
	ExpiryDate: createComponent("expiryDate"),
};
